// SidebarFoot — the left menu's foot (pen Q82 "Search and a minimal left
// menu", decided 3 Nov), pure.
//
// A divider, then one "you" row: avatar, name and a settings glyph in the
// slot where Sign out sat. The whole row is one button that opens Settings
// (current on the Settings page); Theme, Language, Sign out and the version
// live there. Folded to the rail: the avatar alone, the same button, named
// by its tooltip.
//
// Props-driven: the one intent reports up.

export type { FootAccount };
export { SidebarFoot };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { AccountIdentity } from "../../app/AppShellHost/chrome/accountIdentity.js";
import { Icon } from "../Icon/Icon.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";

// Who is signed in: not known yet (the auth probe is out), or known.
type FootAccount =
    | { readonly type: "loading" }
    | { readonly type: "known"; readonly identity: AccountIdentity };

type SidebarFootProps = {
    readonly expanded: boolean;
    // Settings is the view shown.
    readonly settingsCurrent: boolean;
    readonly onSettings: () => void;
    readonly account: FootAccount;
};

const Avatar = ({ account }: { readonly account: FootAccount }): ReactNode => {
    const initials =
        account.type === "known" && account.identity.type === "named"
            ? account.identity.initials
            : "";
    return (
        <span
            className="bilda-sidebar__avatar"
            data-empty={initials === "" ? "true" : undefined}
            aria-hidden="true"
        >
            {account.type === "known" &&
            account.identity.type === "anonymous" ? (
                <Icon name="person" size="sm" />
            ) : (
                initials
            )}
        </span>
    );
};

const SidebarFoot = ({
    expanded,
    settingsCurrent,
    onSettings,
    account,
}: SidebarFootProps): ReactNode => {
    const { t } = useLingui();
    const name = ((): string => {
        switch (account.type) {
            case "loading":
                return "";
            case "known":
                return account.identity.type === "named"
                    ? account.identity.name
                    : t`Account`;
            default:
                return exhaustSwitchCase(account);
        }
    })();
    return (
        <>
            <div className="bilda-sidebar__divider" aria-hidden="true" />
            <Tooltip content={t`Settings`} side={expanded ? "top" : "right"}>
                <button
                    type="button"
                    className="bilda-sidebar__account"
                    aria-current={settingsCurrent ? "page" : undefined}
                    data-active={settingsCurrent ? "true" : undefined}
                    onClick={onSettings}
                >
                    <Avatar account={account} />
                    {expanded ? (
                        <bdi className="bilda-sidebar__account-name">
                            {name}
                        </bdi>
                    ) : null}
                    {expanded ? (
                        <span
                            className="bilda-sidebar__account-settings"
                            aria-hidden="true"
                        >
                            <Icon name="gear" />
                        </span>
                    ) : null}
                    <VisuallyHidden>{t`Settings`}</VisuallyHidden>
                </button>
            </Tooltip>
        </>
    );
};
